import { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled, { css } from 'styled-components';
import {
  Card,
  CardTitle,
  CardSubtitle,
  CardRow,
  CardBody,
  ListWrapper,
  ListItem,
  ListItemText,
  ListItemPrimaryText,
  ListItemSubtext,
  ListItemCheckbox,
  SearchInput
} from 'cdk-radial';
import { toggleCppForLocationsAndApis } from '../../../../redux/connectedComponent/privacyProtection/privacyProtection.slice';
import BodyContainer from '../bodyContainer/BodyContainer';
import LearnMore from '../../../LearnMore/LearnMore';

import './styles/SelectPrivacyProtection.scss';

const TITLE = 'Configure Consumer Privacy Protection';

const DPCard = styled(Card)`
  box-sizing: border-box;
  border: 1px solid #b8babd;
  box-shadow: none;
  max-height: 600px;
`;
const DPCardHeaderRow = styled(CardRow)`
  padding: 1rem;
  align-items: center;
  justify-content: space-between;
  height: 4.75rem;
  flex-grow: 0;
  flex-shrink: 0;
  border-bottom: 1px solid #b8babd;
`;
const DPCardHeaderGroup = styled(CardBody)`
  height: 4.75rem;
  border-bottom: 1px solid #b8babd;
  flex-grow: 0;
  flex-shrink: 0;
`
const DPScrollingList = styled(ListWrapper)`
  overflow: scroll;
`;
const DPColoredListItem = styled(ListItem)`
  ${({ isSelected, theme }) => isSelected && css`
    &, &:focus {
      background-color: ${theme.color.primary[200].value};
    }
  `}
`;
const DPTallListItem = styled(DPColoredListItem)`
  height: 4.5rem;
`;

function optOutCountForLocation(optOuts, orgId) {
  return Object.entries(optOuts[orgId] || {}).filter(([, v]) => v).length;
}

function optOutCountDisplayForLocation(optOuts, orgId, apiCount) {
  const count = optOutCountForLocation(optOuts, orgId);
  if (!count) {
    return 'No APIs';
  }
  if (count === apiCount) {
    return <><strong>All</strong> APIs</>;
  }
  if (count === 1) {
    return <><strong>1</strong> API</>;
  }
  return <><strong>{count}</strong> APIs</>;
}

function getOptOut(optOuts, locations, apiId) {
  return locations.some(({ entityId }) => optOuts[entityId]?.[apiId]);
}

function areAllOptedOutForApi(optOuts, locations, apiId) {
  return locations.every(({ entityId }) => optOuts[entityId]?.[apiId]);
}

function PrivacyProtectionDescription({ allApisSupported }) {
  return (
    <>
      {allApisSupported ? 'All ' : 'Some '}
      APIs used by this app support Consumer Privacy Protection.
      <br />
      You can limit customer data sharing with the app publisher by opting out.
      {' '}
      <LearnMore docPath="/docs/marketplace/marketplace-for-apps/activating-apps/#configuring-the-consumer-privacy-protection" screen="CPP Configuration"/>
    </>
  );
}

function PrivacyProtectionDealerCard({
  className,
  locations,
  optOuts,
  selectedLocations,
  apiDetails,
  selectLocation,
  selectAllLocations
}) {
  const allSelected = selectedLocations.length === locations.length;
  const apiCount = apiDetails.length;

  const [searchInput, setSearchInput] = useState('');

  const filteredLocations = useMemo(() => locations.filter(
    loc => !searchInput || loc.location.toLowerCase().includes(
      searchInput.toLowerCase()
    )),
    [locations, searchInput]
  );

  return (
    <DPCard className={className} data-testid="location-card">
      <DPCardHeaderRow>
        <CardTitle>Location</CardTitle>
        <SearchInput
          name="privacy-protection-locations-search"
          id="privacy-protection-locations-search"
          placeholder="Search Locations"
          value={searchInput}
          onChange={setSearchInput}
          isAlwaysExpanded
          style={{ width: '50%' }}
        />
      </DPCardHeaderRow>
      <DPScrollingList>
        <DPColoredListItem
          onClick={selectAllLocations}
          isSelected={allSelected}
          showDivider
        >
          <ListItemText>
            <ListItemPrimaryText>All Locations</ListItemPrimaryText>
          </ListItemText>
        </DPColoredListItem>
        {filteredLocations.map(loc => {
          const optOutCount = optOutCountDisplayForLocation(
            optOuts,
            loc.entityId,
            apiCount
          );
          const selected = selectedLocations.includes(loc);
          return (
            <DPTallListItem
              key={loc.entityId}
              onClick={() => selectLocation(loc)}
              isSelected={selected}
            >
              <ListItemText>
                <ListItemPrimaryText>{loc.location}</ListItemPrimaryText>
                <ListItemSubtext>{loc.address}</ListItemSubtext>
                <ListItemSubtext>
                  {optOutCount} opted out
                </ListItemSubtext>
              </ListItemText>
            </DPTallListItem>
          );
        })}
      </DPScrollingList>
    </DPCard>
  );
}

function PrivacyProtectionApiCard({
  locations,
  selectedLocations,
  optOuts,
  apiDetails,
  toggleCpp
}) {
  const allApiIds = apiDetails.map(api => api.id);
  const items = apiDetails.map(api => {
    const checked = getOptOut(optOuts, selectedLocations, api.id);
    const indeterminate = !areAllOptedOutForApi(optOuts, selectedLocations, api.id);
    return { api, checked, indeterminate };
  });

  const allChecked = items.some(i => i.checked);
  const allIndeterminate = items.some(i => !i.checked || i.indeterminate);

  let selectedLocationName = 'Select a location';
  if (selectedLocations.length === locations.length) {
    selectedLocationName = 'All Locations';
  } else if (selectedLocations.length) {
    selectedLocationName = selectedLocations[0].location;
    // add more decorations here if switching to multi selection
  }

  return (
    <DPCard data-testid="api-card">
      <DPCardHeaderGroup>
        <CardTitle>Configure Opt-Out</CardTitle>
        <CardSubtitle data-testid="selected-location-name">
          {selectedLocationName}
        </CardSubtitle>
      </DPCardHeaderGroup>
      {selectedLocations.length ? (
        <DPScrollingList>
          <ListItem
            onClick={() => toggleCpp(selectedLocations, allApiIds)}
            isSelected={allChecked}
            showDivider
          >
            <ListItemCheckbox
              checked={allChecked}
              indeterminate={allIndeterminate}
            />
            <ListItemText>
              <ListItemPrimaryText>All APIs</ListItemPrimaryText>
            </ListItemText>
          </ListItem>
          {items.map(({ api, checked, indeterminate }) => (
            <ListItem
              key={api.id}
              onClick={() => toggleCpp(selectedLocations, api.id)}
              isSelected={checked}
            >
              <ListItemCheckbox
                checked={checked}
                indeterminate={indeterminate}
              />
              <ListItemText>
                <ListItemPrimaryText>{api.name}</ListItemPrimaryText>
              </ListItemText>
            </ListItem>
          ))}
        </DPScrollingList>
      ) : null}
    </DPCard>
  );
}

export default function SelectPrivacyProtection({ solutionData }) {
  const dispatch = useDispatch();
  const cppState = useSelector(state => state.privacyProtectionSlice);
  const sysInfoState = useSelector(state => state.selectDMSSlice);
  const appState = useSelector(state => state.solutionInfoSlice);

  const [selectedLocations, setSelectedLocations] = useState([]);

  const [privacyProtectionApis, allApisSupported] = useMemo(() => {
    const solutionInfo = solutionData || appState.solutionInfo;
    const apiDetails = solutionInfo?.registration_details?.api_details || [];
    const supportedApis = Object.entries(sysInfoState.privacyProtectionSupported)
      .filter(([, v]) => v)
      .map(([k]) => apiDetails.find(api => api.id === k))
      // when calling up edit cpp, privacyProtectionSupported may be stale,
      // containing entries that aren't present in our current apiDetails.
      // filter those out until EditPrivacyProtection can call systemInfo again
      // to update privacyProtectionSupported
      .filter(v => v);

    return [supportedApis, supportedApis.length === apiDetails.length];
  }, [sysInfoState.privacyProtectionSupported, solutionData, appState.solutionInfo]);

  return (
    <BodyContainer
      title={TITLE}
      description={
        <PrivacyProtectionDescription allApisSupported={allApisSupported} />
      }
    >
      <div className="select-privacy-protection-body">
        <PrivacyProtectionDealerCard
          locations={cppState.availableLocations}
          optOuts={cppState.optOutsByLocation}
          selectedLocations={selectedLocations}
          apiDetails={privacyProtectionApis}
          // for multi selection, have this toggle whether the location is selected
          // (i.e. add if not present, remove if present)
          selectLocation={loc => setSelectedLocations([loc])}
          // for multi selection, have this set to all if not all are selected,
          // or empty if all are selected
          selectAllLocations={() => setSelectedLocations(cppState.availableLocations)}
        />
        <PrivacyProtectionApiCard
          locations={cppState.availableLocations}
          selectedLocations={selectedLocations}
          optOuts={cppState.optOutsByLocation}
          apiDetails={privacyProtectionApis}
          toggleCpp={(locations, apiId) => {
            const apiIds = Array.isArray(apiId) ? apiId : [apiId];
            dispatch(toggleCppForLocationsAndApis({
              orgIds: locations.map(loc => loc.entityId),
              apiIds
            }));
          }}
        />
      </div>
    </BodyContainer>
  );
}
